<template>
    <div
        class="field position--relative"
        :class="{ 'field--errors': validIfIsAnError }">
        <base-label
            v-if="settings.input.type !== 'checkbox' && settings.label"
            :settings="{ ...getLabelProps }"
        />
        <template v-if="settings.allowFilling || showComponent">
            <base-label
                v-if="settings.input.type === 'checkbox' && settings.label"
                :settings="{
                    ...getLabelProps,
                    showIcon: false
                }"
            />
            <component
                :is="inputComponent"
                :id="settings.input.id"
                :name="settings.input.name"
                :type="settings.input.type"
                :placeholder="settings.input.placeholder"
                :autocomplete="settings.input.browserAutocomplete"
                :reset="settings.reset"
                :searchField="settings.input.searchField"
                :mask="settings.input.mask"
                :isCampaignList="settings.isCampaignList"
                :audioUrl="settings.input.audio"
                :formatsAllowed="settings.input.formatsAllowed"
                :multipleFiles="settings.input.multipleFiles"
                :needsToMatchOption="settings.input.needsToMatchOption"
                v-bind="{
                    ...( settings.input.class && {
                        class: `field__input field__input--base ${settings.input.class}`
                    }),
                    ...( settings.input.targetClass && {
                        targetClass: `field__input field__input--base ${settings.input.targetClass}`
                    }),
                    ...( settings.input.value && { defaultValue: settings.input.value }),
                    ...( !settings.input.noListener && { 'data-listener': true }),
                    ...( ( showComponent && !settings.allowFilling ) && { allowFilling: false }),
                    ...settings.input.dynamicData,
                    listValues: settings.input.list || []
                }"
                v-model="value"
            />
            <div
                v-if="validIfIsAnError"
                class="field--errors__container"
            >
                <icon-alert
                    :class="[
                        'field--errors__alert position--absolute',
                        defineAlertClass( 'alert' )
                    ]"
                />
                <div
                    :class="[
                        'field--errors__tooltip position--absolute background--red color--white capitalize',
                        defineAlertClass( 'tooltip' )
                    ]"
                >
                    {{ getWarnings }}
                </div>
            </div>
        </template>
        <template v-else>
            <div
                v-if="settings.input.component !== 'States'"
                :class="[
                    'field__input--base field__input--disabled display--flex',
                    ...( !settings.input.disableClass && 'field__input--disabled--default' ),
                    ...( settings.input.disableClass && settings.input.disableClass )
                ]"
            >
                {{ getValue }}
            </div>
            <div v-else class="field__input__state--disabled">
                <states :value="settings.input.value" />
            </div>
        </template>
    </div>
</template>

<script>
// vuex
import { mapActions } from 'vuex';

// hooks
import { compareValues } from '@/hooks/use-input-listeners';

export default {
    components: {
        BaseLabel: () => import( /* webpackChunkName: 'IconAlert' */ '@/components/bundles/fields/blocks/Label' ),
        IconAlert: () => import( /* webpackChunkName: 'IconAlert' */ '@/components/bundles/layout/images/icons/IconAlert' ),
        States: () => import( /* webpackChunkName: 'BaseStates' */ '@/components/bundles/states/BaseStates' )
    },
    props: {
        settings: Object
    },
    data () {
        return {
            inputComponent: null,
            validator: {},
            validationParameter: this.settings.input.validationParameter,
            value: this.settings.input.value // assim é evitado que entre no watch.
        };
    },
    validations () {
        return {
            value: {
                ...this.validator
            }
        };
    },
    methods: {
        ...mapActions({
            registerField: 'registerField',
            deleteField: 'deleteField',
            resetError: 'resetError'
        }),
        validations () { // verifica se as validações já terminaram.
            // eslint-disable-next-line
            return new Promise( resolve => {
                // valida se existe erro e
                // se não esta pendente de outra validação
                //    caso sim, então não é necessário esperar.
                //    caso não, fica a escuta que a validação pendente termine.
                if ( this.$v.value.$error || !this.$v.value.$pending )
                    return resolve();

                // de 200mls irá validar se a validação está pendente.
                const listener = setInterval( () => {
                    if ( !this.$v.value.$pending ) {
                        clearInterval( listener );
                        resolve();
                    }
                }, 200 );
            });
        },
        async isValid () { // executa as validações do campo.
            // caso precise para resetar - this.$v.$reset()
            this.$v.value.$touch(); // executa as validações.
            await this.validations(); // espera que termina as validações.
            return Promise.resolve( !this.$v.$error ); // returna se passou ou não nas validações
        }
    },
    computed: {
        validIfIsAnError () {
            // validação se existe algum erro no campo ( validações ou campo em falta ).
            return this.$v.value.$error || this.settings.input.isMissing || this.settings.input.errorOnSubmit?.show;
        },
        getLabelProps () {
            const { settings } = this;

            return {
                name: settings.input.name,
                class: settings.label.class,
                text: settings.label.text,
                isRequired: settings.input.isRequired,
                allowFilling: settings.allowFilling,
                ...( settings.label.isNecessaryPre && { isNecessaryPre: settings.label.isNecessaryPre })
            };
        },
        getWarnings () { // obtém a mensagem de erro.
            if ( this.settings.input.isMissing && !this.$v.value.error )
                return this.$t( 'error.required' );

            if ( this.settings.input.errorOnSubmit?.show )
                return this.settings.input.errorOnSubmit.messages[0];

            return this.settings.input.errors.find( ({ type }) => !this.$v.value[type] ).warning;
        },
        showComponent () {
            // se for um destes componentes, quando o campo está bloqueado é necessário
            // manter a importação do componente, de forma a manter o aspecto e não estar
            // a replicar e com mais if's.
            return [ 'InputMultiOptions', 'InputCheckbox', 'InputToggle' ].includes( this.settings.input.component );
        },
        haveLabel () {
            return !this.settings.label.class.includes( 'field__label--hide' );
        },
        isCalendarInline () {
            return this.settings.input.dynamicData?.isInline;
        },
        getValue () {
            const { input } = this.settings;
            return input.dynamicData?.apiValue?.label || input.value || '---------';
        },
        defineAlertClass () {
            return type => (
                this.settings.alert
                    ? `field--errors__${type}--${this.settings.alert.class}`
                    : !this.isCalendarInline
                        ? this.haveLabel ? `field--errors__${type}--default` : `field--errors__${type}--no-label`
                        : `field--errors__${type}--calendar`
            );
        },
        componentAndName () {
            return this.settings.input.component && this.settings.input.name;
        }
    },
    watch: {
        'settings.input.component': {
            immediate: true,
            deep: false,
            async handler ( newComponent ) {
                // obtém dinamicamente o componente de input.
                this.inputComponent = () => import(
                    /* webpackChunkName: '[request]' */
                    `@/components/dynamic/inputs/${newComponent}`
                ).then( chunk => chunk.default || chunk );
            }
        },
        componentAndName: {
            immediate: true,
            async handler () {
                // valida se o campo tem validações
                //    caso tenha, faz a importação do ficheiro de validações
                if ( this.settings.input.fileValidation ) {
                    this.validator = await import(
                        /* webpackChunkName: '[request]' */
                        `@/validations/${this.settings.input.fileValidation}`
                    ).then( validator => validator.default );
                }
            }
        },
        'settings.input.validationParameter': {
            handler ( newParam ) {
                this.validationParameter = newParam;

                // caso o campo fique com erro,
                // é apagado o valor da store.
                if ( this.$v.$error )
                    this.deleteField({ name: this.settings.input.name });
            }
        },
        value: {
            deep: false,
            async handler ( newValue ) {
                const inputName = this.settings.input.name;
                // caso anteriormente exista um erro
                // associado ao campo, é limpo.
                switch ( true ) {
                    case this.settings.input.isMissing:
                        this.resetError({ type: 'missing', fieldName: inputName });
                        break;
                    case this.settings.input.errorOnSubmit?.show:
                        this.resetError({ type: 'api', fieldName: inputName });
                        break;
                    default:
                }

                // valida se o utilizador esta a editar e
                // se o campo editado é igual ao valor guardado na BD ou
                // se o campo está vázio.
                //    caso não seja, executa as restantes validações.
                //    caso seja, não é necessário executar as restantes validações e
                //               remove o campo da store.
                if (
                    ( !this.settings.isEdition && !newValue )
                    || ( this.settings.isEdition && compareValues( newValue, this.settings.input.value ) )
                ) {
                    this.deleteField({ name: inputName });
                    return;
                }

                // executa as validações ao campo.
                const isValid = await this.isValid();

                // valida se passou em todas as validações.
                //    caso tenha passado, vai guardar o campo em store,
                //                        de forma a dps submeter para a API.
                //    caso não tenha passado, vai remover o campo da store,
                //                            caso ele já esteja armazenado lá.
                ( isValid )
                    ? this.registerField({ name: inputName, value: newValue })
                    : this.deleteField({ name: inputName });

                // caso seja necessário emitir para o pai
                // o valor, de forma a que seja feito
                // algum procedimento.
                if ( this.settings.emitToParent )
                    this.$emit( 'get-value', newValue );
            }
        }
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $input-class: '.field__input';
    $input-autocomplete-class: '.field__autocomplete';
    $input-calendar-class: '.field__calendar';
    $arrow-class: '.field__autocomplete__arrow';
    $eye-class: '.field--password__eye';
    $checkbox-label-class: '.field__checkbox';
    $checkbox-icon-class: '.field__checkbox__icon';
    $switch-label-class: '.field__toggle__label';
    $input-height: 36px;

    // =============================================================================
    // Style
    // =============================================================================
    .field {
        &--errors {
            #{$input-class} {
                background: rgba( $color: $color-red, $alpha: .2 );
                color: $color-red;
                @include placeholder {
                    color: $color-red;
                }
            }
            #{$input-autocomplete-class}::after {
                width: 70px;
                background: rgba( $color: $color-red, $alpha: .2 );
            }
            #{$input-calendar-class} {
                .vc-container {
                    color: $color-red;
                }
            }
            #{$arrow-class} {
                @include transform( translate( -25px, -50% ) );
            }
            & + #{$eye-class} {
                display: none;
            }
            &__alert {
                @include transform-position( false, 12px, false, false, translateY( -50% ) );
                z-index: 3;
                &--default {
                    top: 48px;
                }
                &--no-label {
                    top: 50%;
                }
                &--calendar {
                    top: -14px;
                }
                &--textarea {
                    @include transform-position( 15px, 22px, false, false );
                }
            }
            &__tooltip {
                right: 8px;
                &--default {
                    bottom: calc( 100% - 30px );
                }
                &--no-label {
                    bottom: 45px;
                }
                &--calendar {
                    bottom: 111%;
                }
                &--textarea {
                    @include transform-position( false, 18px, 103%, false );
                }
                max-width: 215px;
                @include typography-specs( 12px, 14px );
                padding: 5px 10px;
                @include rounded-corner( 5px );
                z-index: 2;
                &::before {
                    @include pseudo();
                    @include dimensions( 8px, 8px );
                    background: $color-red;
                    @include transform-position( false, 10px, -4px, false, rotate( 45deg ) );
                }
            }
        }
        &__label {
            margin-bottom: 5px;
            font-weight: 500;
            &--hide {
                display: block;
                height: 0;
                margin-bottom: 0;
                overflow: hidden;
            }
            &__pre {
                white-space: pre-wrap;
                font-family: inherit;
            }
        }
        &__input {
            caret-color: $color-green;
            background-color: $color-grey-light;
            @include rounded-corner( 19px );
            @include placeholder {
                color: rgba( $color-default, .5 );
            }
            &:-webkit-autofill {
                -webkit-box-shadow: 0 0 0 50px $color-grey-light inset;
                -webkit-text-fill-color: $color-default;
            }
            &--default {
                height: $input-height;
            }
            &--base {
                width: 100%;
                @include typography-specs( 18px, 24px );
                padding-left: 15px;
            }
            &--disabled {
                align-items: center;
                border: 1px solid $color-grey-light;
                @include rounded-corner( 19px );
                &--default {
                    min-height: $input-height;
                    height: auto;
                }
            }
        }
        &__autocomplete {
            &::after {
                @include pseudo();
                @include transform-position( 0, -1px, false, false );
                @include dimensions( 45px, $input-height );
                background: $color-grey-light;
                @include rounded-corner( 19px );
                z-index: 3;
                @include transition( all .15s ease-in-out );
                will-change: width, background;
            }
            &__input {
                z-index: 3;
            }
            &__arrow {
                @include transform-position( 50%, 15px, false, false, translateY( -50% ) );
                @include transition( transform .3s ease-in-out );
                will-change: transform;
                z-index: 4;
                &--active {
                    @include transform( rotate( 180deg ) translateY( 50% ) );
                }
            }
            &__options {
                top: 0;
                opacity: 0;
                @include transition( opacity .3s ease-in-out );
                will-change: opacity;
                pointer-events: none;
                &--active {
                    opacity: 1;
                    pointer-events: all;
                    z-index: 2;
                }
                &__content {
                    @include dimensions( 100%, 145px );
                }
            }
        }
        &__textarea {
            width: calc( 100% - 15px );
            padding-top: 10px;
            resize: none;
            &--default {
                height: 200px;
            }
            &--disable {
                padding: 15px;
            }
        }
        &__multi {
            &__input {
                z-index: 1;
                &--disable {
                    background: $color-white;
                }
            }
            &__options {
                top: (-$input-height);
                &__content {
                    @include dimensions( 100%, 170px );
                }
            }
        }
        &__drop {
            width: 100%;
            @include rounded-corner( 19px );
            padding: 50px 0 25px;
            @include box-shadow( 0 -1px 6px $color-black-light );
            &::before {
                @include pseudo();
                top: $input-height;
                @include dimensions( 100%, 1px );
                background: $color-grey;
            }
            &__option {
                min-height: 33px;
                line-height: 33px;
                padding-left: 15px;
                &:hover, &.js--active {
                    background: rgba( $color: $color-dark, $alpha: .34 );
                }
            }
        }
        &__checkbox {
            padding-left: 25px;
            &__icon {
                @include transform-position( 9px, false, false, 0 );
                @include dimensions( 15px, 15px );
                @include rounded-corner( 50% );
                &--field {
                    top: 6px;
                }
                &::before, &::after {
                    @include pseudo();
                    @include transform-position( 0, 0, 0, 0 );
                    @include dimensions( 10px, 1px );
                    @include transition( background .4s ease-in-out, transform .3s ease-in-out );
                    will-change: background, transform;
                    margin: auto;
                }
            }
            &__input {
                &:checked + #{$checkbox-label-class} #{$checkbox-icon-class} {
                    &::before, &::after {
                        background: $color-green;
                        @include transition( background .2s ease-in-out, transform .3s ease-in-out );
                        z-index: 1;
                    }
                    &::before {
                        @include transform( rotate( -45deg ) );
                    }
                    &::after {
                        @include transform( rotate( 45deg ) );
                    }
                }
            }
        }
        &__states {
            &__value {
                &:not(:last-child) {
                    margin-right: 15px;
                }
                &--inactive {
                    opacity: .4;
                    @include transition( opacity .3s ease-in-out );
                    will-change: opacity;
                    &:hover {
                        opacity: 1;
                    }
                }
            }
        }
        &__toggle {
            &__input {
                &:checked + #{$switch-label-class} {
                    background: $color-green;
                    &::after {
                        left: calc( 100% - 5px );
                        transform: translateX( -100% );
                    }
                }
            }
            &__label {
                @include dimensions( 60px, 25px );
                @include rounded-corner( 100px );
                background: $color-grey-light;
                @include transition( background .3s ease-in-out );
                will-change: background;
                &::after {
                    @include pseudo();
                    @include transform-position( 3px, false, false, 5px );
                    @include dimensions( 19px, 19px );
                    background: $color-white;
                    @include rounded-corner( 50% );
                    @include transition( all .3s ease-in-out );
                    will-change: transform, left, width;
                }
                &:active::after {
                    width: 25px;
                }
            }
        }
        &__calendar{
            .vc-container {
                font-family: 'Roboto';
            }
            .vc-title,
            .vc-nav-item {
                text-transform: capitalize;
            }
            .vc-date-time {
                display: flex;
                @include position-grid( center, space-between );
                width: 100%;
            }
            .vc-date {
                order: 2;
                padding: 0 !important;
                margin: 0 !important;
            }
            .vc-time {
                span {
                    color: $color-green;
                }
            }
            .vc-time-icon {
                display: none;
            }
            .vc-select {
                select {
                    background-color: $color-grey-light !important;
                    color: $color-green !important;
                    @include rounded-corner( 26px !important );
                }
                .vc-select-arrow {
                    svg {
                        fill: $color-green !important;
                    }
                }
            }
        }
        &__scroll {
            width: calc( 100% - 19px );
            overflow-y: auto;
        }
    }
</style>